import { useEffect, useState } from 'react';
import { useDesignStore } from '../store/designStore';
import { FULL_COLOR } from '@customedge/shared';
import type { DesignSide } from '../store/designStore';

export interface ColorBudget {
  limit: number | null;      // null = Full Color, i.e. unlimited
  usedColors: string[];      // distinct colors currently on this side's active count (background included)
  remaining: number | null;  // null = unlimited; limit - usedColors.length, floored at 0
  isAtLimit: boolean;        // remaining === 0 (never true when limit === null)
  isColorAllowed: (hex: string) => boolean; // already used, or budget has room
  // The side's ACTIVE count key exactly as resolveActiveColorCountKey resolved
  // it (a numeric string, FULL_COLOR, or '__unset__') — exposed so a caller
  // that needs to key its OWN per-count state (e.g. ColorsUsedPanel.tsx's
  // reserved-slot storage) doesn't have to re-derive this same value with its
  // own, possibly-inconsistent logic.
  countKey: string;
}

const EMPTY: ColorBudget = {
  limit: null, usedColors: [], remaining: null, isAtLimit: false,
  isColorAllowed: () => true, countKey: '__unset__',
};

// Converts a #hex (3 or 6 digit) to "r,g,b" so it compares equal to an
// rgba(...) string representing the identical color — BackgroundTool.tsx
// stores its color background as `rgba(r, g, b, a)` (via hexToRgba), never a
// bare hex, so without this a background set to "#ff0000" and a text fill
// also set to "#ff0000" would be counted as two DIFFERENT budget slots
// instead of recognizing the swatch is already in use. Opacity/alpha is
// deliberately ignored for this comparison — the budget counts distinct
// PRINT colors, and alpha is a canvas-preview-only concept with no print
// equivalent (a semi-transparent red background is still "red" for the
// purposes of a flat print-color count).
//
// This is a COMPARISON key only — "255,0,0" is not valid CSS and must never
// be handed to a caller expecting a displayable/paintable color (a swatch
// background, a `<input type="color">` value, etc). See normalizeColor's own
// comment for why it deliberately returns this same non-displayable form,
// and collectObjectColors below for how a real displayable hex is recovered
// alongside it.
function toComparableRgb(value: string): string | null {
  if (value.startsWith('#')) {
    const h = value.slice(1);
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    if (full.length !== 6 || /[^0-9a-f]/.test(full)) return null;
    const num = parseInt(full, 16);
    return `${(num >> 16) & 255},${(num >> 8) & 255},${num & 255}`;
  }
  if (value.startsWith('rgb')) {
    const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (!m) return null;
    return `${m[1]},${m[2]},${m[3]}`;
  }
  return null;
}

// COMPARISON-ONLY — the returned string ("r,g,b", e.g. "255,0,0") is NOT
// valid CSS and must never be rendered as a swatch color or passed to a
// `<input type="color">`. Used for equality checks (isColorAllowed,
// excludeColor matching) where a hex and its rgba-stored equivalent must
// compare equal. For a real, paintable color, see `collectObjectColors`'s
// `display` map (keeps the first-seen ORIGINAL string per comparison key)
// or `rgbCsvToHex` to convert this format back to a usable hex.
export function normalizeColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed === 'transparent') return null;
  if (trimmed.startsWith('#') || trimmed.startsWith('rgb')) return toComparableRgb(trimmed);
  return null;
}

// Converts normalizeColor's "r,g,b" comparison form back to a real,
// paintable "#rrggbb" hex — for any consumer that needs to actually DISPLAY
// a color (a swatch background, an <input type="color"> value) rather than
// just compare two colors for equality.
export function rgbCsvToHex(rgbCsv: string): string {
  const [r, g, b] = rgbCsv.split(',').map(Number);
  return `#${[r, g, b].map(v => Math.max(0, Math.min(255, v || 0)).toString(16).padStart(2, '0')).join('')}`;
}

// Approximates whether an object's own bounding box overlaps AT ALL with the
// visible design-area rectangle (0,0,canvasWidth,canvasHeight — the canvas
// and the design area are the exact same rectangle in this app, see
// DesignerWorkspace.tsx's guide-box overlay, which is drawn pixel-identical
// to the canvas itself, purely decorative). Nothing in this app clamps an
// object's position after creation (useAlignmentGuides.ts's snap assist only
// nudges objects that come near an edge; a customer can freely drag/resize
// an object fully or partially off-canvas afterward) — so without this
// check, a color dragged out of view would still silently consume a budget
// slot and appear as a "used" suggestion, even though nothing of it is
// actually visible/printed anymore.
//
// A LIVE object exposes `getBoundingRect()` (accounts for rotation/scale/
// origin correctly via Fabric's own math) — prefer that when available. A
// CACHED JSON object (no live Fabric instance to call methods on) only has
// raw left/top/width/height/scaleX/scaleY/originX/originY fields — approximate
// the same rectangle from those directly. This is intentionally an
// axis-aligned approximation (ignores `angle`/rotation for the cached path)
// since a perfect rotated-rect intersection isn't worth the complexity for a
// "does this still touch the visible area at all" budget check — a rotated
// object's true footprint is always at least as large as this unrotated
// approximation, so this can only ever be MORE permissive (never wrongly
// excludes something that's actually visible), which is the safe direction
// to err for a budget check.
function objectOverlapsArea(obj: any, canvasWidth: number, canvasHeight: number): boolean {
  let left: number, top: number, width: number, height: number;
  if (typeof obj?.getBoundingRect === 'function') {
    const rect = obj.getBoundingRect();
    left = rect.left; top = rect.top; width = rect.width; height = rect.height;
  } else {
    const w = (obj?.width ?? 0) * (obj?.scaleX ?? 1);
    const h = (obj?.height ?? 0) * (obj?.scaleY ?? 1);
    const originX = obj?.originX ?? 'left';
    const originY = obj?.originY ?? 'top';
    const rawLeft = obj?.left ?? 0;
    const rawTop = obj?.top ?? 0;
    left = originX === 'center' ? rawLeft - w / 2 : originX === 'right' ? rawLeft - w : rawLeft;
    top = originY === 'center' ? rawTop - h / 2 : originY === 'bottom' ? rawTop - h : rawTop;
    width = w; height = h;
  }
  // Standard axis-aligned rectangle intersection test.
  return left < canvasWidth && left + width > 0 && top < canvasHeight && top + height > 0;
}

// Walks a Fabric object list (either live canvas.getObjects() or a cached
// toJSON()'s `objects` array — same shape either way for the fields this
// reads) collecting every distinct color in use: flat fill/stroke (text,
// shapes) AND, for a placed raster asset (image/clipart/artwork), its own
// `printColors` — the resolved print palette BackgroundColorTool.tsx's
// Select Colors step stamps onto the object once quantized (see that file's
// own commitToCanvas comment). Without reading `printColors` here, every
// placed image/clipart/artwork/shape contributed ZERO colors to the budget —
// the suggestion list and limit enforcement were blind to anything but text
// fills/strokes and the flat background, even though a quantized image's
// colors are just as real a part of "colors used in this design."
//
// Only counts objects that still overlap the visible design area (see
// objectOverlapsArea) — an object dragged fully out of view no longer
// contributes its color(s) to the budget. `canvasWidth`/`canvasHeight` come
// from the `DesignSide` itself (the single source of truth for "the design
// area," see useColorBudget's own side-level comment), not from the live
// canvas's own (possibly zoomed) pixel dimensions.
//
// `into` maps each color's COMPARISON key (normalizeColor's non-displayable
// "r,g,b" form) to a real, PAINTABLE representation of it — preferring a
// genuine "#hex" original over an "rgba(...)" original when both exist for
// the same comparison key, since a plain hex is the more broadly useful
// display form (matches what `<input type="color">` and every swatch button
// here actually need).
function collectObjectColors(objects: any[] | undefined, into: Map<string, string>, canvasWidth: number, canvasHeight: number) {
  const record = (raw: unknown) => {
    if (typeof raw !== 'string') return;
    const key = normalizeColor(raw);
    if (!key) return;
    const existing = into.get(key);
    if (!existing || (!existing.startsWith('#') && raw.trim().startsWith('#'))) {
      into.set(key, raw.trim());
    }
  };
  for (const obj of objects ?? []) {
    if (!objectOverlapsArea(obj, canvasWidth, canvasHeight)) continue;
    record(obj?.fill);
    // A stroke with width 0 renders nothing (Fabric's own default for every
    // freshly-created object) — counting its leftover `stroke` value would
    // consume a budget slot for a color that isn't actually visible/printed.
    const hasVisibleStroke = typeof obj?.strokeWidth === 'number' && obj.strokeWidth > 0;
    if (hasVisibleStroke) record(obj?.stroke);
    if (Array.isArray(obj?.printColors)) {
      for (const c of obj.printColors) record(c);
    }
  }
}

// Live color-count budget for a side: how many distinct colors its active
// Print Color count allows, how many are already used (text/image fills,
// strokes, and a flat background color all count toward the same budget —
// per the product requirement that background occupies one of the N allowed
// colors), and whether a NEW color can still be added. Reacts to live canvas
// edits when the side is the currently-mounted one; falls back to its cached
// snapshot otherwise (e.g. computing another position's budget for display).
//
// `excludeColor` — the color(s) the CALLER'S OWN control/object currently
// represents (e.g. an already-placed object's current fill, or the canvas's
// current background), so changing it to a DIFFERENT color is judged as a
// swap of that slot, not an addition on top of it. Without this, a
// background (or any already-set fill) already counted in `usedColors` would
// make `isAtLimit` true and block picking any other color at all — including
// swapping the background for a different single color while nothing else on
// the budget changed.
//
// Accepts a single color OR an array — a single selected TEXT object has BOTH
// a fill and a stroke, each needing its OWN independent exclusion when
// editing the OTHER (call this hook once per control — see PropertyPanel.tsx,
// which calls it twice — rather than excluding both at once, which would
// wrongly let a fill edit ignore the object's own already-used stroke slot
// too). But a single IMAGE/clipart/artwork object re-entering "Correct
// Colors" can have been quantized to MULTIPLE colors (its whole
// `printColors` array) — ALL of them belong to that one object being
// re-edited and must be excluded together, or its own prior palette would
// count against itself and shrink the room available to re-quantize/match it
// (see BackgroundColorTool.tsx's designColorBudget, the array case this
// exists for).
export function useColorBudget(side: DesignSide | undefined | null, excludeColor?: string | string[]): ColorBudget {
  const { canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey } = useDesignStore();
  // Bumped on every relevant live-canvas event so this hook re-derives
  // usedColors as the customer edits — the store's own cache only updates at
  // side-switch/count-switch boundaries (see useFabricCanvas.ts), not on
  // every keystroke/color pick, so subscribing to the store alone would leave
  // the budget stale until the next switch.
  const [tick, setTick] = useState(0);

  const canvas = side ? canvasInstances.get(side.sideKey) : undefined;

  useEffect(() => {
    if (!canvas) return;
    const bump = () => setTick(n => n + 1);
    canvas.on('object:added', bump);
    canvas.on('object:removed', bump);
    canvas.on('object:modified', bump);
    // Background color/image changes don't fire any of the above — BackgroundTool.tsx
    // calls canvas.set('backgroundColor', ...)/setBackgroundImage() directly, so this
    // hook has to poll rather than depend on a Fabric event to know a background swatch
    // just occupied (or freed) a budget slot the same visit.
    const bgPoll = window.setInterval(bump, 500);
    return () => {
      canvas.off('object:added', bump);
      canvas.off('object:removed', bump);
      canvas.off('object:modified', bump);
      window.clearInterval(bgPoll);
    };
  }, [canvas]);

  if (!side) return EMPTY;

  // `usedColors` is computed regardless of whether a real numeric budget
  // exists — a customer picking a color for new text/background, or a
  // "match design colors" prompt for a placed image, is a genuinely useful
  // suggestion even for a Full-Color or unconfigured product; only the
  // ENFORCEMENT fields below (limit/remaining/isAtLimit/isColorAllowed's
  // blocking behavior) are meaningful exclusively when a real count exists.
  //
  // Keyed by normalizeColor's comparison key ("r,g,b") -> a real, paintable
  // representation of that same color (preferring an original "#hex" over an
  // "rgba(...)" one — see collectObjectColors) — the comparison key itself
  // must NEVER leak into `usedColors`/be handed to a caller expecting a
  // renderable color; it's not valid CSS.
  const countKey = resolveActiveColorCountKey(side);
  const display = new Map<string, string>();
  const recordBackground = (raw: unknown) => {
    if (typeof raw !== 'string') return;
    const key = normalizeColor(raw);
    if (key && !display.has(key)) display.set(key, raw.trim());
  };
  if (canvas) {
    // Live canvas — read straight from Fabric so this reacts to edits made
    // this visit, before they're ever snapshotted into the cache.
    collectObjectColors(canvas.getObjects?.() ?? [], display, side.canvasWidth, side.canvasHeight);
    recordBackground(canvas.backgroundColor);
  } else {
    const cached = sideColorCountJsonCache[side.sideKey]?.[countKey];
    collectObjectColors(cached?.objects, display, side.canvasWidth, side.canvasHeight);
    recordBackground(cached?.background ?? cached?.backgroundColor);
  }
  const usedColors = Array.from(display.values());

  const limit = countKey === FULL_COLOR ? null : Number(countKey);
  if (limit == null || !Number.isFinite(limit)) {
    // Full Color, or '__unset__'/malformed — no enforceable limit, but the
    // real used-colors list (computed above) is still returned so callers
    // can show suggestions without gating on a budget existing.
    return { ...EMPTY, usedColors, countKey };
  }

  const excludeList = excludeColor == null ? [] : Array.isArray(excludeColor) ? excludeColor : [excludeColor];
  const normalizedExcludeSet = new Set(excludeList.map(normalizeColor).filter((k): k is string => k != null));
  // Budget math excludes the caller's own current slot(s) — see excludeColor's
  // param comment. `usedColors` itself (returned for display/"your colors"
  // swatches) intentionally still includes them; only the LIMIT check ignores
  // them. Compares/counts by KEY (display.keys()), never the displayable
  // value — two differently-formatted strings for the identical color (a hex
  // fill and an rgba-stored background) must count as ONE slot, not two.
  const countingKeys = Array.from(display.keys()).filter(k => !normalizedExcludeSet.has(k));
  const remaining = Math.max(0, limit - countingKeys.length);
  const isAtLimit = remaining === 0;

  return {
    limit, usedColors, remaining, isAtLimit, countKey,
    isColorAllowed: (hex: string) => {
      const normalized = normalizeColor(hex);
      if (!normalized) return true; // not a recognizable flat color — nothing to restrict
      if (normalizedExcludeSet.has(normalized)) return true; // no-op change to an excluded slot
      return countingKeys.includes(normalized) || !isAtLimit;
    },
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}

// Finds the first object on the LIVE canvas whose own fill/stroke/printColors
// is using up a real (non-excluded) budget slot — for the "block the new
// pick, then jump straight to the object holding up the limit so its color
// can be changed right there" flow (TextTool.tsx/BackgroundTool.tsx/
// PropertyPanel.tsx/BackgroundColorTool.tsx all call this the moment
// `isColorAllowed` returns false for a customer's pick). Deliberately
// SAME-SIDE only — this app's color budget is scoped per side (confirmed:
// consolidating across sides sharing one print position was explicitly
// ruled out), so the offending object is always already on the canvas the
// customer is looking at; no side-switch is ever needed to reach it.
//
// `excludeObject` — skip the object currently selected/being edited itself
// (e.g. don't "jump to" the very text box whose OWN stroke picker just got
// blocked — that object isn't the problem, something ELSE already used the
// last slot). Returns null when nothing else on canvas is actually holding a
// real slot (e.g. the block was caused by the caller's own object alone,
// which normally shouldn't happen once `excludeColor` is passed correctly,
// but this is a defensive dead end rather than a crash).
export function findOffendingObject(canvas: any, side: DesignSide, excludeObject?: any): any | null {
  if (!canvas || typeof canvas.getObjects !== 'function') return null;
  for (const obj of canvas.getObjects()) {
    if (obj === excludeObject) continue;
    if (!objectOverlapsArea(obj, side.canvasWidth, side.canvasHeight)) continue;
    const hasColor = normalizeColor(obj?.fill)
      || (typeof obj?.strokeWidth === 'number' && obj.strokeWidth > 0 && normalizeColor(obj?.stroke))
      || (Array.isArray(obj?.printColors) && obj.printColors.some((c: unknown) => normalizeColor(c)));
    if (hasColor) return obj;
  }
  return null;
}

// Selects `obj` on `canvas` and syncs the store's `selectedObject` so
// PropertyPanel immediately shows its color control, ready to edit — the
// second half of "jump to the offending object." Mirrors LayerPanel.tsx's
// own `selectLayer` (`canvas.setActiveObject` + `renderAll` + store sync),
// the one existing precedent for "clicking something elsewhere selects an
// object on the live canvas," reused here rather than re-implemented.
export function selectOffendingObject(canvas: any, obj: any): void {
  if (!canvas || !obj) return;
  canvas.setActiveObject(obj);
  canvas.renderAll();
  useDesignStore.getState().setSelectedObject(obj);
}

// Repaints EVERY object (and the canvas background) currently using
// `fromHex` over to `toHex`, in one pass — the "Colors Used in Design" panel
// (ColorsUsedPanel.tsx) and the reworked per-object pickers both call this
// so picking a replacement for a color that's already used elsewhere updates
// every occurrence at once, per direct user request ("if user select any
// color that lead to the color picker. If user change the color it will
// reflect on all the object user the color"). There is no single "color"
// property on a Fabric object — four distinct write shapes exist in this
// app (see the module comment on collectObjectColors for the read-side
// equivalent): a flat `fill`, a `stroke` (only when `strokeWidth > 0` is
// visible — matches collectObjectColors' own rule so this never "fixes" a
// slot that was never actually counted), each entry of a raster asset's
// quantized `printColors` array (BackgroundColorTool.tsx's commitToCanvas),
// and the canvas-level `backgroundColor` (BackgroundTool.tsx, always stored
// as an rgba(...) string via hexToRgba, never a bare hex). Bounds-filtered
// the same way the budget itself is (objectOverlapsArea) — an object dragged
// off the visible design area is already excluded from `usedColors`, so it
// must also be excluded here, or replacing a color could silently repaint an
// object the customer can't currently see/isn't part of "the design."
export function replaceColorEverywhere(canvas: any, side: DesignSide, fromHex: string, toHex: string): void {
  if (!canvas || typeof canvas.getObjects !== 'function') return;
  const fromKey = normalizeColor(fromHex);
  if (!fromKey) return;
  let changed = false;

  for (const obj of canvas.getObjects()) {
    if (!objectOverlapsArea(obj, side.canvasWidth, side.canvasHeight)) continue;
    if (normalizeColor(obj?.fill) === fromKey) { obj.set({ fill: toHex }); changed = true; }
    const hasVisibleStroke = typeof obj?.strokeWidth === 'number' && obj.strokeWidth > 0;
    if (hasVisibleStroke && normalizeColor(obj?.stroke) === fromKey) { obj.set({ stroke: toHex }); changed = true; }
    if (Array.isArray(obj?.printColors)) {
      const nextPrintColors = obj.printColors.map((c: unknown) => normalizeColor(c) === fromKey ? toHex : c);
      if (nextPrintColors.some((c: unknown, i: number) => c !== obj.printColors[i])) {
        obj.set({ printColors: nextPrintColors });
        changed = true;
      }
    }
    obj.setCoords?.();
  }

  if (normalizeColor(canvas.backgroundColor) === fromKey) {
    // Preserves whatever alpha the background currently has — a color swap
    // shouldn't silently make a semi-transparent background fully opaque.
    // hexToRgba's own opacity-percent convention (0-100) is BackgroundTool.tsx's,
    // not reproduced here to avoid a cross-file coupling for one line; the
    // alpha channel is read straight off the existing rgba(...) string
    // instead (0 when the background was a bare hex/no alpha segment, which
    // never happens for this app's own background writer, but is a safe
    // fallback for a background set some other way).
    const alphaMatch = typeof canvas.backgroundColor === 'string' ? canvas.backgroundColor.match(/rgba?\([^)]+,\s*([\d.]+)\)/) : null;
    const alpha = alphaMatch ? Number(alphaMatch[1]) : 1;
    const rgb = toComparableRgb(toHex.startsWith('#') ? toHex : `#${toHex}`);
    if (rgb) {
      canvas.set('backgroundColor', `rgba(${rgb}, ${Number.isFinite(alpha) ? alpha : 1})`);
      changed = true;
    }
  }

  if (changed) canvas.renderAll();
}

// How many OTHER things on `side` currently use `hex` — read-only preview
// mirroring replaceColorEverywhere's own match logic (same fill/visible-
// stroke/printColors/background rules), so a caller can show "this will
// also change N other object(s)" in a confirmation prompt BEFORE actually
// calling replaceColorEverywhere. Kept in sync with that function's
// matching rules by hand (both are short and read side-by-side) rather than
// sharing a single "does this object use this color" predicate, since
// replaceColorEverywhere also needs to WRITE per-property and a shared
// predicate would need to duplicate that property-shape knowledge right
// back out anyway.
//
// `excludeObject` — skip one specific object (e.g. the object a color
// picker is CURRENTLY editing, or an image mid-re-quantize in
// BackgroundColorTool.tsx's "Correct Colors" flow) so its own pre-existing
// use of `hex` doesn't inflate the count. Without this, EVERY confirm would
// wrongly fire — the very slot/object the customer is looking at always
// already "uses" its own current color, so a naive objectCount>0 check
// would count that as a first "other" usage and confirm on every single
// edit, not just ones that genuinely ripple beyond what the customer
// clicked (caught in review: ColorsUsedPanel.tsx's replaceFilled and
// BackgroundColorTool.tsx's recolorSwatch both need this, the former via a
// -1 adjustment where no direct object reference exists to exclude, the
// latter via this param since `existingObject` — when re-editing an
// already-placed image — IS a real object reference on the live canvas).
export function countObjectsUsingColor(canvas: any, side: DesignSide, hex: string, excludeObject?: any): { objectCount: number; usesBackground: boolean } {
  if (!canvas || typeof canvas.getObjects !== 'function') return { objectCount: 0, usesBackground: false };
  const key = normalizeColor(hex);
  if (!key) return { objectCount: 0, usesBackground: false };
  let objectCount = 0;
  for (const obj of canvas.getObjects()) {
    if (obj === excludeObject) continue;
    if (!objectOverlapsArea(obj, side.canvasWidth, side.canvasHeight)) continue;
    const hasVisibleStroke = typeof obj?.strokeWidth === 'number' && obj.strokeWidth > 0;
    const matches = normalizeColor(obj?.fill) === key
      || (hasVisibleStroke && normalizeColor(obj?.stroke) === key)
      || (Array.isArray(obj?.printColors) && obj.printColors.some((c: unknown) => normalizeColor(c) === key));
    if (matches) objectCount++;
  }
  const usesBackground = normalizeColor(canvas.backgroundColor) === key;
  return { objectCount, usesBackground };
}

// Design-wide "most used colors" — the top 10 distinct colors currently in
// use, ranked by how many objects (+ background) use each, across EVERY
// side/count-key this design has content for, not just the currently active
// one. Used to auto-fill a newly-opened color slot when the print-color
// COUNT increases (see ColorsUsedPanel.tsx) — per direct user request ("You
// maintain the Top 10 colors from all the objects you can use those color
// for auto file when the user add more color while editing"), a brand new
// slot should default to a color the customer is already using elsewhere in
// the design, cycling through the next-most-common one not already assigned
// to another slot, rather than every new slot defaulting to the exact same
// single color or an arbitrary one.
//
// Walks `sideColorCountJsonCache` (every side × count-key snapshot this
// design has ever had content for — see designStore.ts's loadFromTemplate/
// useFabricCanvas.ts for how it's populated) PLUS every currently-mounted
// live canvas (so an edit made this visit, not yet flushed to the cache, is
// still reflected) — same dual-source pattern useColorBudget itself follows
// for a single side, generalized across all of them here since "top colors
// in the design" is deliberately NOT scoped to one side/count the way the
// budget itself is.
export function getTopDesignColors(limit = 10): string[] {
  const { sideColorCountJsonCache, canvasInstances } = useDesignStore.getState();
  const freq = new Map<string, number>();
  const display = new Map<string, string>();
  const bump = (raw: unknown) => {
    if (typeof raw !== 'string') return;
    const key = normalizeColor(raw);
    if (!key) return;
    freq.set(key, (freq.get(key) ?? 0) + 1);
    const existing = display.get(key);
    if (!existing || (!existing.startsWith('#') && raw.trim().startsWith('#'))) display.set(key, raw.trim());
  };
  const walkObjects = (objects: any[] | undefined) => {
    for (const obj of objects ?? []) {
      bump(obj?.fill);
      const hasVisibleStroke = typeof obj?.strokeWidth === 'number' && obj.strokeWidth > 0;
      if (hasVisibleStroke) bump(obj?.stroke);
      if (Array.isArray(obj?.printColors)) for (const c of obj.printColors) bump(c);
    }
  };

  for (const bySide of Object.values(sideColorCountJsonCache)) {
    for (const snapshot of Object.values(bySide ?? {})) {
      walkObjects((snapshot as any)?.objects);
      bump((snapshot as any)?.background ?? (snapshot as any)?.backgroundColor);
    }
  }
  for (const canvas of canvasInstances.values()) {
    walkObjects((canvas as any)?.getObjects?.() ?? []);
    bump((canvas as any)?.backgroundColor);
  }

  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([key]) => display.get(key)!)
    .filter(Boolean);
}
